// Centralized replacement for a pattern that used to be duplicated across five
// layout components (StockLayout, SettingsLayout, SetupLayout, StockPrintLayout,
// SuppliersLayout): since the MFE host can change the browser URL without this
// app's own React Router seeing it, each one independently polled
// window.location.pathname AND monkey-patched window.history.pushState /
// replaceState to catch host-driven navigation, restoring "the original" on
// unmount.
//
// Patching a mutable global (window.history.pushState/replaceState) from five
// different components that mount/unmount independently — some nested inside
// others, and all force-remounted together via StockLayout's `key={routerKey}`
// on top-level route-segment changes — is only safe if every mount/unmount
// happens in strict LIFO order. React does not guarantee that here. When the
// order is violated, one layer's unmount-time "restore to what I captured as
// original" clobbers another still-mounted layer's wrapper, silently breaking
// that layer's route sync — with no periodic re-sync able to recover, because
// the very poll/patch that should be closing the gap is what broke. The
// observable symptom: a section's sub-page tab bar (gated on the `pathCheck`
// Redux value tracked by that sync) goes blank and stays blank until a hard
// refresh remounts everything from scratch.
//
// Fix: patch history exactly once, at module scope, and let every layout
// subscribe a plain callback instead of touching window.history itself.

const listeners = new Set()

const notify = () => {
  listeners.forEach((listener) => {
    try {
      listener()
    } catch (e) {
      // One subscriber throwing shouldn't stop the others from running.
      console.error('[routeSyncBridge] listener error', e)
    }
  })
}

let installed = false

const install = () => {
  if (installed) return
  installed = true

  const originalPushState = window.history.pushState
  const originalReplaceState = window.history.replaceState

  window.history.pushState = function (...args) {
    originalPushState.apply(window.history, args)
    setTimeout(notify, 0)
  }

  window.history.replaceState = function (...args) {
    originalReplaceState.apply(window.history, args)
    setTimeout(notify, 0)
  }

  window.addEventListener('popstate', notify)

  // Single shared poll as a fallback safety net (covers any route change that
  // doesn't go through pushState/replaceState/popstate) — 50ms matches the
  // fastest interval any of the layouts previously ran on their own.
  setInterval(notify, 50)
}

// Subscribe to "the browser URL may have changed". Runs `callback` immediately
// once (so callers don't need their own separate initial check) and again on
// every pushState/replaceState/popstate/poll tick after that. Returns an
// unsubscribe function — call it on unmount.
export const subscribeToRouteSync = (callback) => {
  install()
  listeners.add(callback)
  callback()
  return () => {
    listeners.delete(callback)
  }
}
